import React, { useState } from "react";
import TableCell from "@material-ui/core/TableCell";
import useAlertDialog from "../../../../hooks/useAlertDialog";
import { AlertType } from "../../../../hooks/common/useAttachAlertDialog";
import { Checkbox, TableRow } from "@material-ui/core";
import {
	OrderStatus,
	OrderStatusLabelMap,
	PriceDetail,
	VerificationStatus,
	VerificationStatusLabelMap,
} from "../../../../../models/Order";
import moment from "moment";
import {
	BookingTableBodyButton,
	BookingTableBodyDate,
	BookingTableBodyDateAdmin,
	BookingTableBodyStauts,
	TableCellFloatLeft,
	TableCellFloatLeftInner,
	TableCellFloatLeftInnerUser,
	TableCellFloatLeftInnerv2,
	TableCellSmall,
} from "../../styled/DesStyled";
import useCancelOrder from "../../hook/CancelOrder";
import useLoadedUser from "../../../../hooks/auth/useLoadedUser";
import PermissionGate from "../../../../controls/auth/PermissionGate";
import { UserRole } from "../../../../../models/User";
import useFinishOrder from "../../hook/FinishOrder";
import usePaidOrder from "../../hook/PaidOrder";
import { adminVerifyOrderOverride, SettleChargebackAdmin } from "../../../../../services/OrderService";
import { formatOrderCreatedAtForStation } from "../../../../../utils/orderDateDisplay";
import { formatOrderCalendarDateLegacyGuard } from "../../../../../utils/orderCalendarDate";
import CancelBookingDialog, { CANCELLABLE_PAID_STATUSES } from "./cancel/CancelBookingDialog";
import CancelledRefundBadge, { OrderRefundStatus } from "./cancel/CancelledRefundBadge";
import DisputedBadge, { OrderDisputeOverlay } from "./cancel/DisputedBadge";
import RefundCancelledDialog from "./cancel/RefundCancelledDialog";
import ReopenBookingDialog from "./cancel/ReopenBookingDialog";
import { useAdminPermissions } from "../../../../hooks/useAdminPermissions";
import { formatBagSizeBreakdown } from "../../../../../utils/bagSizeBreakdown";

export default function BookingTableBodyAdmin(props: {
	val: PriceDetail;
	/** Nhãn "Disputed" (17/10/2026): đang mở ⇒ ẩn Cancel booking… / Refund…; đã thua ⇒ chỉ còn huỷ KHÔNG hoàn (backend cũng chặn). */
	dispute?: OrderDisputeOverlay | null;
	reload: () => void;
	isSelected: boolean;
	/** Row can be included in bulk selection (email resend + admin verify lists). */
	canSelectBookingRow: boolean;
	onToggleSelection: (orderId: number) => void;
	setIsDialogLoginOpen: (val: boolean) => void;
	setOrderId: (val: number) => void;
	setIsDialogIfOpen: (val: boolean) => void;
	setOrderIdInfo: (val: number) => void;
}) {
	const [load, setLoad] = useState(false);
	const [verifyLoad, setVerifyLoad] = useState(false);
	const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
	const { hasPermission } = useAdminPermissions();
	const rowStatus = Number(props.val?.status);
	const isDisputed = !!props.dispute?.label;
	// Thua dispute (tiền khách đã lấy lại qua cổng) ⇒ vẫn được huỷ KHÔNG hoàn; đang mở thì khoá hẳn.
	const disputeLost = isDisputed && props.dispute?.phase === "lost";
	// Đã trả trạm (cần "Chargeback settled…") thì Cancel booking… bị chặn ở backend ⇒ ẩn cho đỡ rối.
	const canCancelPaid =
		CANCELLABLE_PAID_STATUSES.includes(rowStatus) && (!isDisputed || disputeLost) && !props.dispute?.needsChargebackSettle;
	const [refundDialogOpen, setRefundDialogOpen] = useState(false);
	// "Cancelled · No refund" (or a failed refund) on a paid booking ⇒ can still be refunded.
	const canRefundCancelled =
		rowStatus === OrderStatus.Cancel &&
		!!(props.val as any)?.paidAt &&
		[OrderRefundStatus.Idle, OrderRefundStatus.Terminated].includes(Number(props.val?.refundStatus)) &&
		!(props.val as any)?.refundPaymentId &&
		!isDisputed;
	// Same booking can instead be reopened (26/10/2026) — backend re-checks every condition.
	const [reopenDialogOpen, setReopenDialogOpen] = useState(false);
	const canReopenCancelled = canRefundCancelled && !props.dispute?.needsChargebackSettle;
	const { user } = useLoadedUser();
	const [anchorEl, setAnchorEl] = useState(null);
	const { openAlertDialog } = useAlertDialog();
	const { doCancelOrder } = useCancelOrder({
		reload: props.reload,
		user: user,
		setLoad: setLoad,
	});
	const { doPaidOrder } = usePaidOrder({ reload: props.reload, user: user, setLoad: setLoad });
	const { doFinishOrder } = useFinishOrder({ reload: props.reload, user: user, setLoad: setLoad });
	const customer = (props.val.customer ||
		(props.val as any).user ||
		(props.val as any).customerUser ||
		(props.val as any).customerInfo) as any;
	const customerName =
		customer?.fullName ||
		customer?.name ||
		(props.val as any).fullName ||
		(props.val.email ? props.val.email.split("@")[0] : "") ||
		"--";
	const customerEmail = customer?.email || props.val.email || "--";
	const customerPhone = customer?.phone || props.val.phone || "--";
	const inferredPrefixFromPhone = `${customerPhone || ""}`.trim().match(/^\+\d{1,4}/)?.[0] || "";
	const customerPhonePrefix =
		customer?.phonePrefix || (props.val as any).phonePrefix || inferredPrefixFromPhone || "--";

	const handleUpdateStatusStaff = async (id: number) => {
		openAlertDialog?.(
			AlertType.Confirm,
			"You definitely want to cancel your order?",
			() => {},
			() => doCancelOrder(id || 0),
			undefined,
			{
				declineText: "Cancel",
				acceptText: "Confirm",
			}
		);
	};

	// Chargeback thua mà đơn đã trả trạm (Quân chốt 17/10/2026): trừ trạm TRƯỚC, rồi mới chốt đơn.
	const canSettleChargeback =
		!!props.dispute?.needsChargebackSettle &&
		![OrderRefundStatus.Begin, OrderRefundStatus.Done].includes(Number(props.val?.refundStatus));
	const handleSettleChargeback = (id: number) => {
		openAlertDialog?.(
			AlertType.Confirm,
			`Booking #${id}: the customer won the chargeback and the station was already paid. ` +
				`Did you already create a Balance adjustment that DEDUCTS the station's share, with "#${id}" in the reason? ` +
				`Confirm to mark this booking Cancelled · Refunded.`,
			() => {},
			async () => {
				try {
					await SettleChargebackAdmin(id);
					openAlertDialog?.(AlertType.Success, `Booking #${id} marked Cancelled · Refunded (chargeback).`, () => props.reload());
				} catch (error: any) {
					openAlertDialog?.(AlertType.Fail, error?.response?.data?.message || error?.message || "Could not settle.", () => {});
				}
			},
			undefined,
			{ declineText: "Cancel", acceptText: "Confirm" }
		);
	};

	const handlePaidOrder = async (id: number) => {
		openAlertDialog?.(
			AlertType.Confirm,
			"Confirmation of payment?",
			() => {},
			() => doPaidOrder(id || 0),
			undefined,
			{
				declineText: "Cancel",
				acceptText: "Confirm",
			}
		);
	};

	const handleFinishOrder = async (id: number) => {
		openAlertDialog?.(
			AlertType.Confirm,
			"Confirm completed application?",
			() => {},
			() => doFinishOrder(id || 0),
			undefined,
			{
				declineText: "Cancel",
				acceptText: "Confirm",
			}
		);
	};

	const handleUpdateOrder = async (id: number) => {
		props.setIsDialogLoginOpen(true);
		props.setOrderId(id);
	};

	const handleInfpOrder = async (id: number) => {
		props.setIsDialogIfOpen(true);
		props.setOrderIdInfo(id);
	};

	const showsVerificationColumn = [
		OrderStatus.Paid,
		OrderStatus.Finish,
		OrderStatus.Reserved,
		OrderStatus.NoShow,
	].includes(Number(props.val.status));
	const canOverrideVerification =
		user?.role === UserRole.Admin && [OrderStatus.Reserved, OrderStatus.NoShow].includes(Number(props.val.status));
	const isReservedAwaitingVerification = Number(props.val.status) === OrderStatus.Reserved;

	const getVerificationLabelText = () => {
		if (!showsVerificationColumn) return null;
		if (Number(props.val.status) === OrderStatus.Paid) {
			return VerificationStatusLabelMap.get(VerificationStatus.Verified) || "Verified";
		}
		if (Number(props.val.status) === OrderStatus.Reserved) {
			return props.val.verificationStatus != null && props.val.verificationStatus !== undefined
				? VerificationStatusLabelMap.get(props.val.verificationStatus) || "Pending"
				: "Pending";
		}
		if (Number(props.val.status) === OrderStatus.NoShow) {
			return (
				VerificationStatusLabelMap.get(props.val.verificationStatus ?? VerificationStatus.NoShow) || "No-Show"
			);
		}
		if (props.val.verificationStatus != null && props.val.verificationStatus !== undefined) {
			return VerificationStatusLabelMap.get(props.val.verificationStatus) || "—";
		}
		return "—";
	};

	const submitVerificationOverride = async (status: VerificationStatus) => {
		const id = props.val.id || 0;
		if (!id) return;
		setVerifyLoad(true);
		try {
			await adminVerifyOrderOverride(id, status);
			openAlertDialog?.(AlertType.Success, "Đã cập nhật trạng thái xác minh.", () => {});
			props.reload();
		} catch (error: any) {
			openAlertDialog?.(
				AlertType.Fail,
				error?.response?.data?.message || error?.message || "Không thể cập nhật.",
				() => {}
			);
		} finally {
			setVerifyLoad(false);
		}
	};

	const confirmVerificationOverride = (status: VerificationStatus, label: string) => {
		openAlertDialog?.(
			AlertType.Confirm,
			`Đặt booking #${props.val.id} thành "${label}"?`,
			() => {},
			() => submitVerificationOverride(status),
			undefined,
			{ declineText: "Hủy", acceptText: "Xác nhận" }
		);
	};

	return (
		<>
			<TableRow>
				<TableCellSmall align="center">
					<Checkbox
						color="primary"
						checked={props.isSelected}
						disabled={!props.canSelectBookingRow}
						onChange={() => props.onToggleSelection(props.val.id || 0)}
						inputProps={{ "aria-label": `select booking ${props.val.id}` }}
					/>
				</TableCellSmall>
				<TableCellSmall align="center">{props.val.id}</TableCellSmall>
				<TableCellSmall align="center">
					{" "}
					<TableCellFloatLeft>
						<TableCellFloatLeftInner>Tên:</TableCellFloatLeftInner>{" "}
						<TableCellFloatLeftInnerv2>{props.val.partner?.name}</TableCellFloatLeftInnerv2>
					</TableCellFloatLeft>{" "}
					<TableCellFloatLeft>
						<TableCellFloatLeftInner>Tên hiển thị:</TableCellFloatLeftInner>{" "}
						<TableCellFloatLeftInnerv2>{props.val.partner?.displayName}</TableCellFloatLeftInnerv2>
					</TableCellFloatLeft>{" "}
					<TableCellFloatLeft>
						<TableCellFloatLeftInner>Địa chỉ:</TableCellFloatLeftInner>{" "}
						<TableCellFloatLeftInnerv2>{props.val.partner?.address}</TableCellFloatLeftInnerv2>
					</TableCellFloatLeft>{" "}
					<TableCellFloatLeft>
						<TableCellFloatLeftInner>Địa chỉ hiển thị:</TableCellFloatLeftInner>{" "}
						<TableCellFloatLeftInnerv2>{props.val.partner?.displayAddress}</TableCellFloatLeftInnerv2>
					</TableCellFloatLeft>
				</TableCellSmall>
				<TableCellSmall align="center">
					<TableCellFloatLeft>
						<TableCellFloatLeftInnerUser>Tên:</TableCellFloatLeftInnerUser>{" "}
						<TableCellFloatLeftInnerv2>{customerName}</TableCellFloatLeftInnerv2>
					</TableCellFloatLeft>
					<TableCellFloatLeft>
						<TableCellFloatLeftInnerUser>Email:</TableCellFloatLeftInnerUser>
						<TableCellFloatLeftInnerv2>{customerEmail}</TableCellFloatLeftInnerv2>
					</TableCellFloatLeft>
					<TableCellFloatLeft>
						<TableCellFloatLeftInnerUser>SDT:</TableCellFloatLeftInnerUser>{" "}
						<TableCellFloatLeftInnerv2>{customerPhone}</TableCellFloatLeftInnerv2>
					</TableCellFloatLeft>
					<TableCellFloatLeft>
						<TableCellFloatLeftInnerUser>Mã:</TableCellFloatLeftInnerUser>{" "}
						<TableCellFloatLeftInnerv2>{customerPhonePrefix}</TableCellFloatLeftInnerv2>
					</TableCellFloatLeft>
				</TableCellSmall>
				<TableCellSmall align="center">
					{props.val.quantity}
					{formatBagSizeBreakdown(props.val) ? (
						<div style={{ fontSize: 11, color: "#6B7280", whiteSpace: "nowrap" }}>
							{formatBagSizeBreakdown(props.val)}
						</div>
					) : null}
				</TableCellSmall>
				<TableCellSmall align="center">
					{Math.round((props.val.totalMoney || 0) * 100) / 100} USD
				</TableCellSmall>
				<TableCellSmall align="center">
					<BookingTableBodyDateAdmin>
						{formatOrderCalendarDateLegacyGuard(
							props.val.timeInString,
							props.val.timeIn,
							props.val.timeZoneId
						)}
					</BookingTableBodyDateAdmin>
				</TableCellSmall>
				<TableCellSmall align="center">
					<BookingTableBodyDateAdmin>
						{formatOrderCalendarDateLegacyGuard(
							props.val.timeOutString,
							props.val.timeOut,
							props.val.timeZoneId
						)}
					</BookingTableBodyDateAdmin>
				</TableCellSmall>
				<TableCellSmall align="center">
					<BookingTableBodyStauts status={props.val?.status}>
						{" "}
						{OrderStatusLabelMap.get(props.val?.status || 0)}
					</BookingTableBodyStauts>
					{rowStatus === OrderStatus.Cancel && <CancelledRefundBadge refundStatus={props.val?.refundStatus} />}
					<DisputedBadge dispute={props.dispute} />
				</TableCellSmall>
				<TableCellSmall align="center">
					<div style={{ fontSize: 13 }}>
						{showsVerificationColumn ? (
							<>
								<div style={{ fontWeight: 600, marginBottom: 6 }}>{getVerificationLabelText()}</div>
								{canOverrideVerification && (
									<PermissionGate permission="orders.edit">
										<div
											style={{
												display: "flex",
												flexDirection: "column",
												gap: 6,
												alignItems: "center",
											}}>
											<BookingTableBodyButton
												status={1}
												loading={verifyLoad}
												onClick={() =>
													confirmVerificationOverride(VerificationStatus.Verified, "Verified")
												}>
												{verifyLoad ? "…" : "Verified"}
											</BookingTableBodyButton>
											{isReservedAwaitingVerification && (
												<BookingTableBodyButton
													status={1}
													loading={verifyLoad}
													onClick={() =>
														confirmVerificationOverride(
															VerificationStatus.NoShow,
															"No-Show"
														)
													}>
													No-Show
												</BookingTableBodyButton>
											)}
										</div>
									</PermissionGate>
								)}
							</>
						) : (
							<span style={{ color: "#9ca3af" }}>—</span>
						)}
					</div>
				</TableCellSmall>
				<TableCellSmall align="center">
					<BookingTableBodyDateAdmin>
						{formatOrderCreatedAtForStation(props.val.createdAt, props.val.timeZoneId)}
					</BookingTableBodyDateAdmin>
				</TableCellSmall>

				<TableCellSmall align="center">
					{user?.role != UserRole.Partner && (
						<>
							{/* Unpaid booking: nothing to refund — keep the old one-click "Hủy". */}
							{rowStatus === OrderStatus.Pending && (
								<PermissionGate permission="orders.cancel">
									<BookingTableBodyButton
										loading={load}
										status={props.val?.status}
										onClick={() => handleUpdateStatusStaff(props.val.id || 0)}>
										{load ? "Loading ..." : "Hủy"}
									</BookingTableBodyButton>
								</PermissionGate>
							)}
							{/* Paid booking: one entry point, refund decision forced (16/10/2026). */}
							{user?.role === UserRole.Admin && canCancelPaid && (
								<PermissionGate anyOf={["orders.cancel", "orders.refund"]}>
									<div
										role="button"
										onClick={() => setCancelDialogOpen(true)}
										style={{
											height: 32,
											padding: "0 10px",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											borderRadius: 8,
											border: "1px solid #FCA5A5",
											color: "#B91C1C",
											background: "#fff",
											cursor: "pointer",
											fontWeight: 600,
											marginBottom: 4,
											whiteSpace: "nowrap",
										}}>
										Cancel booking…
									</div>
									<CancelBookingDialog
										open={cancelDialogOpen}
										order={props.val}
										canRefund={hasPermission("orders.refund") && !disputeLost}
										disputeLost={disputeLost}
										refundBlockedReason={
											disputeLost
												? "Dispute lost — the customer already got this money back through the bank / PayPal. Cancel without refund."
												: undefined
										}
										onClose={() => setCancelDialogOpen(false)}
										onDone={(message) => {
											setCancelDialogOpen(false);
											openAlertDialog?.(AlertType.Success, message, () => props.reload());
										}}
									/>
								</PermissionGate>
							)}
							{user?.role === UserRole.Admin && canSettleChargeback && (
								<PermissionGate permission="orders.refund">
									<div
										role="button"
										onClick={() => handleSettleChargeback(props.val.id || 0)}
										title="Chargeback lost after the station was paid: deduct the station first (Balance adjustment with #booking in the reason), then confirm here."
										style={{
											height: 32,
											padding: "0 10px",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											borderRadius: 8,
											border: "1px solid #FDA4AF",
											color: "#9F1239",
											background: "#fff",
											cursor: "pointer",
											fontWeight: 600,
											marginBottom: 4,
											whiteSpace: "nowrap",
										}}>
										Chargeback settled…
									</div>
								</PermissionGate>
							)}
							{user?.role === UserRole.Admin && canRefundCancelled && (
								<PermissionGate permission="orders.refund">
									<div
										role="button"
										onClick={() => setRefundDialogOpen(true)}
										style={{
											height: 32,
											padding: "0 10px",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											borderRadius: 8,
											border: "1px solid #FCA5A5",
											color: "#B91C1C",
											background: "#fff",
											cursor: "pointer",
											fontWeight: 600,
											marginBottom: 4,
											whiteSpace: "nowrap",
										}}>
										Refund…
									</div>
									<RefundCancelledDialog
										open={refundDialogOpen}
										order={props.val}
										onClose={() => setRefundDialogOpen(false)}
										onDone={(message) => {
											setRefundDialogOpen(false);
											openAlertDialog?.(AlertType.Success, message, () => props.reload());
										}}
									/>
								</PermissionGate>
							)}
							{user?.role === UserRole.Admin && canReopenCancelled && (
								<PermissionGate anyOf={["orders.verify", "orders.edit"]}>
									<div
										role="button"
										onClick={() => setReopenDialogOpen(true)}
										style={{
											height: 32,
											padding: "0 10px",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											borderRadius: 8,
											border: "1px solid #5EEAD4",
											color: "#0F766E",
											background: "#fff",
											cursor: "pointer",
											fontWeight: 600,
											marginBottom: 4,
											whiteSpace: "nowrap",
										}}>
										Reopen booking…
									</div>
									<ReopenBookingDialog
										open={reopenDialogOpen}
										order={props.val}
										onClose={() => setReopenDialogOpen(false)}
										onDone={(message) => {
											setReopenDialogOpen(false);
											openAlertDialog?.(AlertType.Success, message, () => props.reload());
										}}
									/>
								</PermissionGate>
							)}
							{user?.role == UserRole.Admin && (
								<PermissionGate permission="orders.edit">
									<BookingTableBodyButton
										status={1}
										onClick={() => handleUpdateOrder(props.val.id || 0)}>
										Sửa
									</BookingTableBodyButton>
								</PermissionGate>
							)}
							{props.val?.status == 1 && (
								<PermissionGate permission="orders.edit">
									<BookingTableBodyButton
										status={1}
										onClick={() => handlePaidOrder(props.val.id || 0)}>
										Trả tiền
									</BookingTableBodyButton>
								</PermissionGate>
							)}
							{props.val?.status == 2 && (
								<PermissionGate permission="orders.edit">
									<BookingTableBodyButton
										status={1}
										onClick={() => handleFinishOrder(props.val.id || 0)}>
										Hoàn thành
									</BookingTableBodyButton>
								</PermissionGate>
							)}
							<BookingTableBodyButton
								status={1}
								onClick={() => handleInfpOrder(props.val.id || 0)}>
								Thông tin
							</BookingTableBodyButton>
						</>
					)}
				</TableCellSmall>
			</TableRow>
		</>
	);
}
